// frontend/src/hooks/useAssetInventoryData.js
import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { assetInventoryAPI, staffAPI } from '../services/api';
import toast from '../utils/toast';
import { parseApiValidationErrors } from '../utils/apiErrors';

// ============================================
// DASHBOARD & STATS
// ============================================

export const useAssetDashboard = () => {
  return useQuery({
    queryKey: ['assets', 'dashboard'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getAssetDashboard();
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// ============================================
// ENDPOINT SECURITY (Trend Micro coverage + licensing)
// ============================================

export const useEndpointSecuritySummary = () => {
  return useQuery({
    queryKey: ['assets', 'endpoint-security', 'summary'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getEndpointSecuritySummary();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useFleetMaintenanceAnalytics = (params = {}) => {
  return useQuery({
    queryKey: ['fleet', 'maintenance-analytics', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getFleetMaintenanceAnalytics(params);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useDownloadEndpointSecurityReport = () => {
  return useMutation({
    mutationFn: async () => {
      const response = await assetInventoryAPI.downloadEndpointSecurityReport();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match?.[1]
        || `TrendMicro_Endpoint_Licensing_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return filename;
    },
    onSuccess: () => toast.success('Licensing report downloaded'),
    onError: (error) => {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to generate report');
    },
  });
};

export const useSyncFleetVehicleCompliance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fleetVehicleId }) =>
      assetInventoryAPI.syncFleetVehicleCompliance(fleetVehicleId).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['fleet'] });
      toast.success('Road tax and inspection dates synced from Ops Portal');
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.error
        || 'Could not sync compliance dates from Ops Portal'
      );
    },
  });
};

// ============================================
// ASSET CATEGORIES
// ============================================

export const useAssetCategories = (params = {}) => {
  return useQuery({
    queryKey: ['assets', 'categories', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getCategories(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - categories rarely change
  });
};

export const useCreateAssetCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets', 'categories'] });
      toast.success('Category created successfully');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to create category');
    },
  });
};

export const useUpdateAssetCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => assetInventoryAPI.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets', 'categories'] });
      toast.success('Category updated successfully');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to update category');
    },
  });
};

// ============================================
// ASSET TYPES
// ============================================

export const useAssetTypes = (params = {}) => {
  return useQuery({
    queryKey: ['assets', 'types', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getAssetTypes(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useCreateAssetType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.createAssetType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets', 'types'] });
      toast.success('Asset type created successfully');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to create asset type');
    },
  });
};

export const useUpdateAssetType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => assetInventoryAPI.updateAssetType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets', 'types'] });
      toast.success('Asset type updated successfully');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to update asset type');
    },
  });
};

// ============================================
// ASSETS
// ============================================

export const useAssets = (params = {}) => {
  return useQuery({
    queryKey: ['assets', 'list', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getAssets(params);
      return {
        results: response.data?.results || response.data || [],
        count: response.data?.count || 0,
      };
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useAsset = (id) => {
  return useQuery({
    queryKey: ['assets', 'detail', id],
    queryFn: async () => {
      const response = await assetInventoryAPI.getAsset(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useAvailableAssets = (params = {}) => {
  return useQuery({
    queryKey: ['assets', 'available', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getAvailableAssets(params);
      return response.data || [];
    },
    staleTime: 1 * 60 * 1000, // 1 minute - availability changes frequently
  });
};

export const useTechRefreshAssets = () => {
  return useQuery({
    queryKey: ['assets', 'tech-refresh'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getTechRefreshAssets();
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useExpiringWarranties = (days = 90) => {
  return useQuery({
    queryKey: ['assets', 'expiring-warranties', days],
    queryFn: async () => {
      const response = await assetInventoryAPI.getExpiringWarranties(days);
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useAssetHistory = (assetId) => {
  return useQuery({
    queryKey: ['assets', 'history', assetId],
    queryFn: async () => {
      const response = await assetInventoryAPI.getAssetHistory(assetId);
      return response.data || [];
    },
    enabled: !!assetId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useCreateAsset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.createAsset(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      toast.success('Asset created successfully');
    },
    onError: (error) => {
      toast.error(parseApiValidationErrors(error?.response?.data) || 'Failed to create asset');
    },
  });
};

export const useUpdateAsset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => assetInventoryAPI.updateAsset(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      if (variables?.id) {
        queryClient.invalidateQueries({ queryKey: ['assets', 'detail', variables.id] });
      }
      if (variables?.showSuccessToast !== false) {
        toast.success('Asset updated successfully');
      }
    },
    onError: (error) => {
      const message = parseApiValidationErrors(error) || 'Failed to update asset';
      toast.error(message);
    },
  });
};

// ============================================
// ASSET ACTIONS
// ============================================

export const useAssignAsset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.assignAsset(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      toast.success('Asset assigned successfully');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to assign asset');
    },
  });
};

export const useBulkAssignAssets = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.bulkAssignAssets(data),
    onSuccess: (response) => {
      const count = response?.data?.count || 0;
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      toast.success(`${count} asset(s) assigned successfully`);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to bulk-assign assets');
    },
  });
};

export const useTransferAsset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.transferAsset(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      toast.success('Asset transferred successfully');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to transfer asset');
    },
  });
};

export const useReturnToInventory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.returnToInventory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      toast.success('Asset returned to inventory');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to return asset');
    },
  });
};

/**
 * Deactivate an access-control credential (door access / facial recognition).
 * Used by the on-/after-end-date "Deactivate access" action in the asset list.
 */
export const useDeactivateAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => assetInventoryAPI.deactivateAccess(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      toast.success('Access deactivated');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to deactivate access');
    },
  });
};

export const useReturnToLessor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => assetInventoryAPI.returnToLessor(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      const lessor = response?.data?.lease_returned_to;
      toast.success(
        lessor
          ? `Returned to ${lessor}. It is no longer in active inventory.`
          : 'Returned to the lessor. It is no longer in active inventory.'
      );
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to record the return');
    },
  });
};

export const useMarkForDisposal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => assetInventoryAPI.markForDisposal(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      toast.success('Asset marked for disposal');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to mark for disposal');
    },
  });
};

// ============================================
// TRANSFERS
// ============================================

export const useAssetTransfers = (params = {}) => {
  return useQuery({
    queryKey: ['assets', 'transfers', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getTransfers(params);
      return {
        results: response.data?.results || response.data || [],
        count: response.data?.count || 0,
      };
    },
    staleTime: 2 * 60 * 1000,
  });
};

export const useTransfer = (transferId, options = {}) => {
  return useQuery({
    queryKey: ['assets', 'transfer', transferId],
    queryFn: async () => {
      const response = await assetInventoryAPI.getTransfer(transferId);
      return response.data;
    },
    enabled: !!transferId && (options.enabled !== false),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
};

/**
 * Append supporting photos to an existing transfer (custodian/HR only).
 * Used by TransferDetailModal for post-completion documentation.
 */
export const useAddTransferPhotos = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transferId, photos }) =>
      assetInventoryAPI.addTransferPhotos(transferId, photos),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      toast.success('Photos added to transfer');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to add photos');
    },
  });
};

export const useRecentTransfers = (limit = 20) => {
  return useQuery({
    queryKey: ['assets', 'transfers', 'recent', limit],
    queryFn: async () => {
      const response = await assetInventoryAPI.getRecentTransfers(limit);
      return response.data || [];
    },
    staleTime: 1 * 60 * 1000,
  });
};

// ============================================
// CHECKLISTS
// ============================================

export const useChecklists = (params = {}) => {
  return useQuery({
    queryKey: ['checklists', 'list', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getChecklists(params);
      return {
        results: response.data?.results || response.data || [],
        count: response.data?.count || 0,
      };
    },
    staleTime: 2 * 60 * 1000,
  });
};

export const useChecklist = (id) => {
  return useQuery({
    queryKey: ['checklists', 'detail', id],
    queryFn: async () => {
      const response = await assetInventoryAPI.getChecklist(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
};

export const usePendingChecklists = (params = {}) => {
  return useQuery({
    queryKey: ['checklists', 'pending', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getPendingChecklists(params);
      return response.data || [];
    },
    staleTime: 1 * 60 * 1000,
  });
};

export const useChecklistSummary = (id) => {
  return useQuery({
    queryKey: ['checklists', 'summary', id],
    queryFn: async () => {
      const response = await assetInventoryAPI.getChecklistSummary(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
};

export const useEmployeeAssetSummary = (employeeId) => {
  return useQuery({
    queryKey: ['checklists', 'employee-summary', employeeId],
    queryFn: async () => {
      const response = await assetInventoryAPI.getEmployeeAssetSummary(employeeId);
      return response.data;
    },
    enabled: !!employeeId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useDepartmentPendingItems = (departmentId) => {
  return useQuery({
    queryKey: ['checklists', 'department-pending', departmentId],
    queryFn: async () => {
      const response = await assetInventoryAPI.getDepartmentPendingItems(departmentId);
      return response.data || [];
    },
    enabled: !!departmentId,
    staleTime: 1 * 60 * 1000,
  });
};

export const useCreateChecklist = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.createChecklist(data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      const type = variables.checklist_type === 'onboarding' ? 'Onboarding' : 'Offboarding';
      toast.success(`${type} checklist created successfully`);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to create checklist');
    },
  });
};

// ============================================
// SOFTWARE LICENSES
// ============================================

export const useSoftwareLicenses = (params = {}) => {
  return useQuery({
    queryKey: ['licenses', 'list', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getLicenses(params);
      return {
        results: response.data?.results || response.data || [],
        count: response.data?.count || 0,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useSoftwareLicense = (id) => {
  return useQuery({
    queryKey: ['licenses', 'detail', id],
    queryFn: async () => {
      const response = await assetInventoryAPI.getLicense(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useExpiringLicenses = (days = 30) => {
  return useQuery({
    queryKey: ['licenses', 'expiring', days],
    queryFn: async () => {
      const response = await assetInventoryAPI.getExpiringLicenses(days);
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useLicenseSeats = (licenseId) => {
  return useQuery({
    queryKey: ['licenses', 'seats', licenseId],
    queryFn: async () => {
      const response = await assetInventoryAPI.getLicenseSeats(licenseId);
      return response.data || [];
    },
    enabled: !!licenseId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useAssignLicenseSeat = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ licenseId, data }) => assetInventoryAPI.assignLicenseSeat(licenseId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      queryClient.invalidateQueries({ queryKey: ['licenses', 'seats', variables.licenseId] });
      toast.success('License seat assigned');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to assign license seat');
    },
  });
};

export const useRevokeLicenseSeat = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ licenseId, data }) => assetInventoryAPI.revokeLicenseSeat(licenseId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      queryClient.invalidateQueries({ queryKey: ['licenses', 'seats', variables.licenseId] });
      toast.success('License seat revoked');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to revoke license seat');
    },
  });
};

// ============================================
// MAINTENANCE LOGS
// ============================================

export const useMaintenanceLogs = (params = {}) => {
  return useQuery({
    queryKey: ['maintenance', 'list', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getMaintenanceLogs(params);
      return {
        results: response.data?.results || response.data || [],
        count: response.data?.count || 0,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateMaintenanceLog = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.createMaintenanceLog(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      toast.success('Maintenance log created');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to create maintenance log');
    },
  });
};

// ============================================
// EMPLOYEE SELF-SERVICE (MY ASSETS)
// ============================================

/**
 * Get all assets assigned to the current user
 */
export const useMyAssets = () => {
  return useQuery({
    queryKey: ['my-assets', 'list'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getMyAssets();
      return response.data || [];
    },
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Get a summary of user's assets and pending transfers
 */
export const useMyAssetsSummary = () => {
  return useQuery({
    queryKey: ['my-assets', 'summary'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getMyAssetsSummary();
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Get pending transfers awaiting the user's acceptance
 */
export const usePendingIncomingTransfers = () => {
  return useQuery({
    queryKey: ['my-assets', 'pending-incoming'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getPendingIncomingTransfers();
      return response.data || [];
    },
    staleTime: 1 * 60 * 1000, // Check more frequently
  });
};

/**
 * Get pending transfers the user has initiated
 */
export const usePendingOutgoingTransfers = () => {
  return useQuery({
    queryKey: ['my-assets', 'pending-outgoing'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getPendingOutgoingTransfers();
      return response.data || [];
    },
    staleTime: 1 * 60 * 1000,
  });
};

/**
 * Get pending-receipt assigns awaiting the user's acknowledgment
 */
export const usePendingReceiptTransfers = () => {
  return useQuery({
    queryKey: ['my-assets', 'pending-receipt'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getPendingReceiptTransfers();
      return response.data || [];
    },
    staleTime: 1 * 60 * 1000,
  });
};

/**
 * Initiate a transfer to another employee
 */
export const useInitiateTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, toEmployeeId, notes }) => 
      assetInventoryAPI.initiateTransfer(assetId, { to_employee_id: toEmployeeId, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      toast.success('Transfer initiated. Waiting for recipient to accept.');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to initiate transfer');
    },
  });
};

/**
 * Accept a pending transfer
 */
export const useAcceptTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transferId, condition, photos }) => 
      assetInventoryAPI.acceptTransfer(transferId, { condition, photos }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      toast.success('Transfer accepted. Asset is now in your possession.');
    },
    onError: (error) => {
      const errorData = error?.response?.data;
      const errorCode = errorData?.error_code;
      const errorMessage = errorData?.error || 'Failed to accept transfer';
      
      // Handle different error types with appropriate messages
      if (errorCode === 'PERMISSION_DENIED') {
        toast.error('Unable to save photos. Please contact IT support to check media directory permissions.');
      } else if (errorCode === 'VALIDATION_ERROR') {
        toast.error(errorMessage);
      } else if (error?.response?.status === 503) {
        toast.error('Service temporarily unavailable. Please try again or contact support.');
      } else {
        toast.error(errorMessage);
      }
    },
  });
};

/**
 * Acknowledge receipt of an asset (pending-receipt assign)
 */
export const useAcknowledgeReceipt = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transferId, condition = '', photos }) =>
      assetInventoryAPI.acknowledgeReceipt(transferId, { condition, photos }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      toast.success('Receipt acknowledged. Asset is now in your My Assets.');
    },
    onError: (error) => {
      const errorData = error?.response?.data;
      const errorCode = errorData?.error_code;
      const errorMessage = errorData?.error || 'Failed to acknowledge receipt';
      
      // Handle different error types with appropriate messages
      if (errorCode === 'PERMISSION_DENIED') {
        toast.error('Unable to save photos. Please contact IT support to check media directory permissions.');
      } else if (errorCode === 'VALIDATION_ERROR') {
        toast.error(errorMessage);
      } else if (error?.response?.status === 503) {
        toast.error('Service temporarily unavailable. Please try again or contact support.');
      } else {
        toast.error(errorMessage);
      }
    },
  });
};

/**
 * Decline a pending transfer or assign (reject)
 */
export const useRejectTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transferId, reason }) =>
      assetInventoryAPI.rejectTransfer(transferId, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      toast.success('Transfer declined');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to reject transfer');
    },
  });
};

/**
 * Cancel a pending transfer (sender only)
 */
export const useCancelTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transferId, reason }) => 
      assetInventoryAPI.cancelTransfer(transferId, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['assets', 'transfers'] });
      toast.success('Transfer cancelled');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to cancel transfer');
    },
  });
};

// ============================================
// EMPLOYEE RETURN TO INVENTORY
// ============================================

/**
 * Get pending return requests initiated by the employee
 */
export const usePendingEmployeeReturns = () => {
  return useQuery({
    queryKey: ['my-assets', 'pending-returns'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getPendingEmployeeReturns();
      return response.data || [];
    },
    staleTime: 1 * 60 * 1000,
  });
};

/**
 * Get transaction history for the Transactions tab (past transfers/returns). Paginated.
 * Returns { items, hasNextPage, isLoading, fetchNextPage }.
 */
export const useTransactionHistory = (pageSize = 20) => {
  const [items, setItems] = useState([]);
  const [nextPage, setNextPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setItems([]);
    setNextPage(1);
    setHasNextPage(true);
    setIsLoading(true);
    assetInventoryAPI.getTransactionHistory(1, pageSize)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data || {};
        setItems(d.results || []);
        setHasNextPage(d.has_next ?? false);
        setNextPage(2);
      })
      .catch(() => { if (!cancelled) { setHasNextPage(false); } })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [pageSize]);

  const fetchNextPage = useCallback(() => {
    if (!hasNextPage || isLoading) return;
    setIsLoading(true);
    assetInventoryAPI.getTransactionHistory(nextPage, pageSize)
      .then((res) => {
        const d = res?.data || {};
        setItems((prev) => [...prev, ...(d.results || [])]);
        setHasNextPage(d.has_next ?? false);
        setNextPage((p) => p + 1);
      })
      .finally(() => setIsLoading(false));
  }, [nextPage, hasNextPage, isLoading, pageSize]);

  return { items, hasNextPage, isLoading, fetchNextPage };
};

/**
 * Initiate a return to inventory (employee)
 */
export const useInitiateReturn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, returnReason, notes }) => 
      assetInventoryAPI.initiateReturn(assetId, { return_reason: returnReason, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      toast.success('Return request submitted. Waiting for custodian to confirm.');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to submit return request');
    },
  });
};

/**
 * Cancel a pending return request (employee)
 */
export const useCancelReturn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transferId, reason }) =>
      assetInventoryAPI.cancelReturn(transferId, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      toast.success('Return request cancelled');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to cancel return request');
    },
  });
};

// ============================================
// CUSTODIAN PENDING RETURNS
// ============================================

/**
 * Get pending returns for custodian's categories
 */
export const usePendingReturnsForCustodian = () => {
  return useQuery({
    queryKey: ['pending-returns', 'custodian'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getPendingReturnsForCustodian();
      const data = response?.data;
      return Array.isArray(data) ? data : [];
    },
    staleTime: 1 * 60 * 1000,
  });
};

/**
 * Confirm receipt of a returned asset (custodian)
 */
export const useConfirmReturn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transferId, location, condition, photos }) =>
      assetInventoryAPI.confirmReturn(transferId, { location, condition, photos }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-returns'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      toast.success('Return confirmed. Asset is now in inventory.');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to confirm return');
    },
  });
};

/**
 * Reject a return request (custodian)
 */
export const useRejectReturn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transferId, reason }) => 
      assetInventoryAPI.rejectReturn(transferId, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-returns'] });
      toast.success('Return request rejected');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to reject return');
    },
  });
};


// ============================================
// VENDORS
// ============================================

export const useVendors = (params = {}) => {
  return useQuery({
    queryKey: ['vendors', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getVendors(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateVendor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.createVendor(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || 'Failed to create vendor';
      toast.error(msg);
    },
  });
};

export const useUpdateVendor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => assetInventoryAPI.updateVendor(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || 'Failed to update vendor';
      toast.error(msg);
    },
  });
};

// ============================================
// INSURANCE PROVIDERS
// ============================================

export const useInsuranceProviders = (params = {}) => {
  return useQuery({
    queryKey: ['insurance-providers', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getInsuranceProviders(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateInsuranceProvider = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.createInsuranceProvider(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurance-providers'] });
      toast.success('Insurance provider saved');
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || 'Failed to save insurance provider';
      toast.error(msg);
    },
  });
};

export const useUpdateInsuranceProvider = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => assetInventoryAPI.updateInsuranceProvider(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurance-providers'] });
      toast.success('Insurance provider updated');
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || 'Failed to update insurance provider';
      toast.error(msg);
    },
  });
};

// ============================================
// VEHICLE LESSORS
// ============================================

export const useVehicleLessors = (params = {}) => {
  return useQuery({
    queryKey: ['vehicle-lessors', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getVehicleLessors(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateVehicleLessor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.createVehicleLessor(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-lessors'] });
      toast.success('Vehicle lessor saved');
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || 'Failed to save vehicle lessor';
      toast.error(msg);
    },
  });
};

export const useUpdateVehicleLessor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => assetInventoryAPI.updateVehicleLessor(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-lessors'] });
      toast.success('Vehicle lessor updated');
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || 'Failed to update vehicle lessor';
      toast.error(msg);
    },
  });
};

// ============================================
// WORKSHOPS
// ============================================

export const useWorkshops = (params = {}) => {
  return useQuery({
    queryKey: ['workshops', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getWorkshops(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateWorkshop = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.createWorkshop(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workshops'] });
      toast.success('Workshop saved');
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || 'Failed to save workshop';
      toast.error(msg);
    },
  });
};

export const useUpdateWorkshop = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => assetInventoryAPI.updateWorkshop(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workshops'] });
      toast.success('Workshop updated');
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || 'Failed to update workshop';
      toast.error(msg);
    },
  });
};

// ============================================
// SERVICE PARTS
// ============================================

export const useServiceParts = (params = {}) => {
  return useQuery({
    queryKey: ['service-parts', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getServiceParts(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useCreateServicePart = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.createServicePart(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-parts'] });
      toast.success('Service part saved');
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || 'Failed to save service part';
      toast.error(msg);
    },
  });
};

export const useUpdateServicePart = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => assetInventoryAPI.updateServicePart(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-parts'] });
      toast.success('Service part updated');
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || 'Failed to update service part';
      toast.error(msg);
    },
  });
};

// ============================================
// SEND / RETURN FROM WORKSHOP
// ============================================

export const useSendToWorkshop = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, ...data }) =>
      assetInventoryAPI.sendToWorkshop(assetId, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      toast.success('Vehicle sent to workshop');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to send to workshop');
    },
  });
};

export const useReturnFromWorkshop = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, ...data }) =>
      assetInventoryAPI.returnFromWorkshop(assetId, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      toast.success('Vehicle returned from workshop');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to return from workshop');
    },
  });
};

// ============================================
// CUSTODIAN MANAGEMENT (HR Admin only)
// ============================================

export const useCustodians = () => {
  return useQuery({
    queryKey: ['staff', 'custodians'],
    queryFn: async () => {
      const response = await staffAPI.getCustodians();
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useUpdateCustodianCategories = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ staffId, categoryIds }) =>
      staffAPI.updateCustodianCategories(staffId, categoryIds).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff', 'custodians'] });
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || 'Failed to update custodian';
      toast.error(msg);
    },
  });
};

// ============================================
// MONTHLY VEHICLE INSPECTIONS — driver self-service (My Assets)
// ============================================

export const useMyInspectionsDue = () => {
  return useQuery({
    queryKey: ['vehicle-inspections', 'my-due'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getMyInspectionsDue();
      return response.data || [];
    },
    staleTime: 2 * 60 * 1000,
  });
};

export const useInspectionTemplate = (options = {}) => {
  return useQuery({
    queryKey: ['vehicle-inspections', 'template'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getInspectionTemplate();
      return response.data;
    },
    staleTime: 30 * 60 * 1000,
    ...options,
  });
};

export const useSubmitVehicleInspection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, ...data }) =>
      assetInventoryAPI.submitVehicleInspection(assetId, data).then((r) => r.data),
    onSuccess: (inspection) => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-inspections'] });
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      if (inspection?.odometer_status === 'review_required') {
        toast.warning('Inspection submitted; odometer sent for custodian review');
      } else {
        toast.success('Inspection submitted');
      }
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || error?.response?.data?.error
        || 'Failed to submit inspection';
      toast.error(msg);
    },
  });
};

// ============================================
// MONTHLY VEHICLE INSPECTIONS — custodian / admin dashboard
// ============================================

export const useVehicleInspections = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['vehicle-inspections', 'list', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getVehicleInspections(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 2 * 60 * 1000,
    ...options,
  });
};

export const useVehicleInspectionDetail = (id, options = {}) => {
  return useQuery({
    queryKey: ['vehicle-inspections', 'detail', id],
    queryFn: async () => {
      const response = await assetInventoryAPI.getVehicleInspection(id);
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useCorrectVehicleInspectionOdometer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      assetInventoryAPI.correctVehicleInspectionOdometer(id, data).then((r) => r.data),
    onSuccess: (inspection) => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-inspections'] });
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      if (inspection?.id) {
        queryClient.setQueryData(
          ['vehicle-inspections', 'detail', inspection.id],
          inspection,
        );
      }
      const cleared = inspection?.odometer_km == null;
      toast.success(
        cleared
          ? 'Odometer reading cleared — drivers can submit against the new floor'
          : `Odometer updated to ${Number(inspection.odometer_km).toLocaleString('en-SG')} km`,
      );
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to correct odometer');
    },
  });
};

export const useInspectionsDueStatus = (params = {}) => {
  return useQuery({
    queryKey: ['vehicle-inspections', 'due-status', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getInspectionsDueStatus(params);
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });
};

export const useInspectionExceptions = (params = {}) => {
  return useQuery({
    queryKey: ['vehicle-inspections', 'exceptions', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getInspectionExceptions(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 2 * 60 * 1000,
  });
};

// ============================================
// TRAFFIC OFFENCE NOTICES — custodian
// ============================================
//
// Short staleTime throughout: these are deadline-driven records where a stale
// view can mean a missed statutory deadline.

export const useTrafficOffences = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['traffic-offences', 'list', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getTrafficOffences(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 60 * 1000,
    ...options,
  });
};

export const useTrafficOffenceDetail = (id, options = {}) => {
  return useQuery({
    queryKey: ['traffic-offences', 'detail', id],
    queryFn: async () => {
      const response = await assetInventoryAPI.getTrafficOffence(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 60 * 1000,
    ...options,
  });
};

export const useTrafficOffenceSummary = (options = {}) => {
  return useQuery({
    queryKey: ['traffic-offences', 'summary'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getTrafficOffenceSummary();
      return response.data;
    },
    staleTime: 60 * 1000,
    ...options,
  });
};

export const useTrafficOffenceSyncStatus = (options = {}) => {
  return useQuery({
    queryKey: ['traffic-offences', 'sync-status'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getTrafficOffenceSyncStatus();
      return response.data;
    },
    staleTime: 60 * 1000,
    ...options,
  });
};

/**
 * Preview which driver custody records place in a vehicle at a given time.
 * Used when (re)proposing a driver after a dispute.
 */
export const useOffenceDriverSuggestion = (vehicleNumber, offenceDatetime, options = {}) => {
  return useQuery({
    queryKey: ['traffic-offences', 'suggest-driver', vehicleNumber, offenceDatetime],
    queryFn: async () => {
      const response = await assetInventoryAPI.suggestOffenceDriver({
        vehicle_number: vehicleNumber,
        offence_datetime: offenceDatetime,
      });
      return response.data;
    },
    enabled: !!vehicleNumber && !!offenceDatetime,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Driver particulars for the offline declaration.
 *
 * Deliberately NOT prefetched — each call writes a PDPA audit entry naming the
 * custodian who viewed the driver's NRIC, so it must fire only on an explicit
 * user action. Enable it when the custodian opens the declaration panel.
 */
export const useOffenceParticulars = (noticeId, enabled = false) => {
  return useQuery({
    queryKey: ['traffic-offences', 'particulars', noticeId],
    queryFn: async () => {
      const response = await assetInventoryAPI.getOffenceParticulars(noticeId);
      return response.data;
    },
    enabled: !!noticeId && enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
};

const invalidateOffences = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['traffic-offences'] });
};

export const useSyncTrafficOffences = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => assetInventoryAPI.syncTrafficOffencesFromOps().then((r) => r.data),
    onSuccess: (result) => {
      invalidateOffences(queryClient);
      const created = result?.created ?? 0;
      const updated = result?.updated ?? 0;
      toast.success(
        created || updated
          ? `Synced — ${created} new, ${updated} updated`
          : 'Synced — no changes'
      );
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to sync from Ops');
    },
  });
};

export const useSetOffenceDeclarationDeadline = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, declaration_deadline }) =>
      assetInventoryAPI
        .setOffenceDeclarationDeadline(id, declaration_deadline)
        .then((r) => r.data),
    onSuccess: () => {
      invalidateOffences(queryClient);
      toast.success('Declaration deadline saved');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to save deadline');
    },
  });
};

export const useProposeOffenceDriver = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      assetInventoryAPI.proposeOffenceDriver(id, data).then((r) => r.data),
    onSuccess: (declaration) => {
      invalidateOffences(queryClient);
      toast.success(`${declaration?.driver_name || 'Driver'} asked to confirm`);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to propose driver');
    },
  });
};

export const useMarkOffenceSubmitted = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      assetInventoryAPI.markOffenceSubmitted(id, data).then((r) => r.data),
    onSuccess: () => {
      invalidateOffences(queryClient);
      toast.success('Declaration recorded — driver notified to pay');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to record declaration');
    },
  });
};

export const useUploadOffencePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file, paymentReference }) =>
      assetInventoryAPI.uploadOffencePayment(id, file, paymentReference).then((r) => r.data),
    onSuccess: () => {
      invalidateOffences(queryClient);
      toast.success('Receipt uploaded');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to upload receipt');
    },
  });
};

export const useVerifyOffencePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      assetInventoryAPI.verifyOffencePayment(id, data).then((r) => r.data),
    onSuccess: () => {
      invalidateOffences(queryClient);
      toast.success('Payment verified — notice closed');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to verify payment');
    },
  });
};

export const useCancelTrafficOffence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) =>
      assetInventoryAPI.cancelTrafficOffence(id, reason).then((r) => r.data),
    onSuccess: () => {
      invalidateOffences(queryClient);
      toast.info('Notice cancelled');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to cancel notice');
    },
  });
};

// ============================================
// TRAFFIC OFFENCE NOTICES — driver self-service
// ============================================

export const useMyOffences = (options = {}) => {
  return useQuery({
    queryKey: ['my-assets', 'offences'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getMyOffences();
      return response.data || [];
    },
    staleTime: 60 * 1000,
    ...options,
  });
};

export const useRespondToOffence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => assetInventoryAPI.respondToOffence(data).then((r) => r.data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      invalidateOffences(queryClient);
      if (variables?.confirmed) {
        toast.success('Confirmed — the fine will be reissued in your name');
      } else {
        toast.info('Dispute submitted — the fleet custodian will follow up');
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to submit your response');
    },
  });
};

export const useUploadMyOffenceReceipt = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noticeId, file, paymentReference }) =>
      assetInventoryAPI.uploadMyOffenceReceipt(noticeId, file, paymentReference)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      invalidateOffences(queryClient);
      toast.success('Receipt uploaded — awaiting verification');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to upload receipt');
    },
  });
};

// ============================================
// VEHICLE ACCIDENTS — custodian
// ============================================
//
// Short staleTime: the insurer clock is measured in hours, so a stale queue
// view can mean a missed statutory notification window.

const invalidateAccidents = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['vehicle-accidents'] });
  queryClient.invalidateQueries({ queryKey: ['my-assets', 'accidents'] });
};

export const useVehicleAccidents = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['vehicle-accidents', 'list', params],
    queryFn: async () => {
      const response = await assetInventoryAPI.getVehicleAccidents(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 60_000,
    ...options,
  });
};

export const useVehicleAccident = (id, options = {}) => {
  return useQuery({
    queryKey: ['vehicle-accidents', 'detail', id],
    queryFn: async () => {
      const response = await assetInventoryAPI.getVehicleAccident(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 60_000,
    ...options,
  });
};

export const useAccidentSummary = (options = {}) => {
  return useQuery({
    queryKey: ['vehicle-accidents', 'summary'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getAccidentSummary();
      return response.data;
    },
    staleTime: 60_000,
    ...options,
  });
};

export const useAccidentVehicleStats = (fleetVehicleId, options = {}) => {
  return useQuery({
    queryKey: ['vehicle-accidents', 'vehicle-stats', fleetVehicleId],
    queryFn: async () => {
      const response = await assetInventoryAPI.getAccidentVehicleStats({
        fleet_vehicle_id: fleetVehicleId,
      });
      return response.data;
    },
    enabled: !!fleetVehicleId,
    staleTime: 60_000,
    ...options,
  });
};

export const useDerivePoliceRequired = (triggers = {}, options = {}) => {
  const {
    injury_level = 'none',
    pedestrian_or_cyclist_involved = false,
    hit_and_run = false,
    foreign_vehicle_involved = false,
    government_property_damaged = false,
  } = triggers;
  return useQuery({
    queryKey: [
      'vehicle-accidents', 'derive-police-required',
      injury_level, pedestrian_or_cyclist_involved, hit_and_run,
      foreign_vehicle_involved, government_property_damaged,
    ],
    queryFn: async () => {
      const response = await assetInventoryAPI.derivePoliceRequired({
        injury_level,
        pedestrian_or_cyclist_involved,
        hit_and_run,
        foreign_vehicle_involved,
        government_property_damaged,
      });
      return response.data;
    },
    staleTime: 60_000,
    ...options,
  });
};

export const useReportAccident = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      assetInventoryAPI.createVehicleAccident(data).then((r) => r.data),
    onSuccess: (accident) => {
      invalidateAccidents(queryClient);
      toast.success(
        accident?.reference
          ? `Accident ${accident.reference} recorded`
          : 'Accident recorded'
      );
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || error?.response?.data?.error
        || 'Failed to record accident';
      toast.error(msg);
    },
  });
};

export const useMarkInsurerNotified = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      assetInventoryAPI.markAccidentInsurerNotified(id, data).then((r) => r.data),
    onSuccess: () => {
      invalidateAccidents(queryClient);
      toast.success('Insurer notification recorded');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to record insurer notification');
    },
  });
};

export const useSetReportingBasis = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, basis, no_reporting_reason }) =>
      assetInventoryAPI.setAccidentReportingBasis(id, { basis, no_reporting_reason })
        .then((r) => r.data),
    onSuccess: () => {
      invalidateAccidents(queryClient);
      toast.success('Reporting basis updated');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to set reporting basis');
    },
  });
};

export const useRecordPoliceReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      assetInventoryAPI.recordAccidentPoliceReport(id, data).then((r) => r.data),
    onSuccess: () => {
      invalidateAccidents(queryClient);
      toast.success('Police report recorded');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to record police report');
    },
  });
};

export const useUploadAccidentSas = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }) =>
      assetInventoryAPI.uploadAccidentSas(id, file).then((r) => r.data),
    onSuccess: () => {
      invalidateAccidents(queryClient);
      toast.success('SAS document uploaded');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to upload SAS');
    },
  });
};

export const useAddAccidentPhotos = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, photos, photoType, caption }) =>
      assetInventoryAPI.addAccidentPhotos(id, photos, { photoType, caption })
        .then((r) => r.data),
    onSuccess: () => {
      invalidateAccidents(queryClient);
      toast.success('Photos uploaded');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to upload photos');
    },
  });
};

export const useRecordAccidentClaim = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      assetInventoryAPI.recordAccidentClaim(id, data).then((r) => r.data),
    onSuccess: () => {
      invalidateAccidents(queryClient);
      toast.success('Claim details updated');
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || error?.response?.data?.error
        || 'Failed to update claim';
      toast.error(msg);
    },
  });
};

export const useLinkAccidentRepair = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, maintenance_log_id }) =>
      assetInventoryAPI.linkAccidentRepair(id, maintenance_log_id).then((r) => r.data),
    onSuccess: () => {
      invalidateAccidents(queryClient);
      toast.success('Repair linked to accident');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to link repair');
    },
  });
};

export const useChangeAccidentDriver = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, driver_staff_id }) =>
      assetInventoryAPI.changeAccidentDriver(id, driver_staff_id).then((r) => r.data),
    onSuccess: (accident) => {
      invalidateAccidents(queryClient);
      toast.success(
        accident?.driver_display_name
          ? `Driver updated to ${accident.driver_display_name}`
          : 'Driver updated'
      );
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to change driver');
    },
  });
};

export const useCloseAccident = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => assetInventoryAPI.closeAccident(id).then((r) => r.data),
    onSuccess: () => {
      invalidateAccidents(queryClient);
      toast.success('Accident closed');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to close accident');
    },
  });
};

export const useCancelAccident = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) =>
      assetInventoryAPI.cancelAccident(id, reason).then((r) => r.data),
    onSuccess: () => {
      invalidateAccidents(queryClient);
      toast.info('Accident cancelled');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to cancel accident');
    },
  });
};

// ============================================
// VEHICLE ACCIDENTS — driver self-service
// ============================================

export const useMyAccidents = (options = {}) => {
  return useQuery({
    queryKey: ['my-assets', 'accidents'],
    queryFn: async () => {
      const response = await assetInventoryAPI.getMyAccidents();
      return response.data || [];
    },
    staleTime: 60_000,
    ...options,
  });
};

export const useReportMyAccident = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      assetInventoryAPI.reportMyAccident(data).then((r) => r.data),
    onSuccess: (accident) => {
      invalidateAccidents(queryClient);
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      toast.success(
        accident?.reference
          ? `Accident ${accident.reference} reported`
          : 'Accident reported'
      );
    },
    onError: (error) => {
      const msg = parseApiValidationErrors(error) || error?.response?.data?.error
        || 'Failed to report accident';
      toast.error(msg);
    },
  });
};

export const useUploadMyAccidentPhotos = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accidentId, photos, photoType, caption }) =>
      assetInventoryAPI.uploadMyAccidentPhotos(accidentId, photos, { photoType, caption })
        .then((r) => r.data),
    onSuccess: () => {
      invalidateAccidents(queryClient);
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      toast.success('Photos uploaded');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to upload photos');
    },
  });
};

export const useUploadMyAccidentSas = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accidentId, file }) =>
      assetInventoryAPI.uploadMyAccidentSas(accidentId, file).then((r) => r.data),
    onSuccess: () => {
      invalidateAccidents(queryClient);
      queryClient.invalidateQueries({ queryKey: ['my-assets'] });
      toast.success('SAS document uploaded');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to upload SAS');
    },
  });
};
